import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Badge } from "@/components/ui";
import { Download, FileText, DollarSign, Layers, Repeat, Sparkles, TrendingUp } from "lucide-react";

async function downloadPdf(key, name) {
  try {
    const res = await api.get(`/docs/${key}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("PDF export failed"); }
}

const tiers = [
  ["0 – 150,000", "$4.25", "up to $7.65M"],
  ["150,001 – 350,000", "$3.75", "~$11.2M"],
  ["350,001 – 600,000", "$3.10", "~$17.7M"],
  ["600,001 – 1,000,000", "$2.75", "~$26.4M"],
  ["1,000,001+", "$2.40", "$28.8M+"],
];
const grid = [
  ["Implementation (phased)", "$2,400,000", "3 phases; core by Feb 2027"],
  ["Ongoing PMPM (base, all core modules)", "$4.25 PMPM", "Volume tiers; upgrades & support included"],
  ["Perpetual License (optional)", "$14.5M + 20%/yr", "Offered, not recommended"],
  ["Upgrades / Maintenance", "Included", "Quarterly releases, regulatory updates"],
  ["4× Growth impact", "$3.10 PMPM", "Net revenue still ~3×"],
  ["10× Growth impact", "$2.40 PMPM", "Architected for 300%+ growth"],
];
const upsells = [
  ["Go MCO AI Suite", "+$0.85 PMPM", "Care-plan drafting, summaries, letters, next-best-action"],
  ["Contact Center Cloud", "+$0.60 PMPM", "Omnichannel — the 'one-stop shop'"],
  ["Interoperability Gateway", "$180k/yr", "FHIR, HL7, X12, MDM, mapping engine"],
  ["Advanced Analytics & BI", "+$0.45 PMPM", "Custom report builder, embedded warehouse"],
  ["Member Engagement App", "$120k/yr + $0.35 PMPM", "Branded mobile, messaging, scheduling"],
  ["End-to-End Billing Engine", "+$0.70 PMPM", "837 intake, 835 posting, rate setting"],
  ["Regulatory Content Packs", "$60k/yr each", "NC DHHS, NCQA, state screening libraries"],
  ["Premium SLA", "+8% of PMPM", "99.95%, 24×7, penalty-backed"],
];
const retainers = [
  ["Configuration-as-a-Service", "$18,000/mo"],
  ["Dedicated CSM", "$9,500/mo"],
  ["Clinical Content & Compliance", "$12,000/mo"],
  ["Managed Reporting Desk", "$7,500/mo"],
  ["Onshore BPO pods", "from $14k/FTE/mo"],
  ["24×7 Command-Center Support", "$15,000/mo"],
];
const angles = [
  "Value-based / shared-savings pricing tied to HEDIS & medical-cost savings",
  "Per-transaction EDI/claims micro-fees ($0.06–$0.12)",
  "Governed, opt-in de-identified benchmarking (client retains data ownership)",
  "App marketplace revenue share (15–30%) via public API/SDK",
  "Training & 'Certified Administrator' certification ($1,800/seat)",
  "White-label / OEM licensing to other MCOs, TPAs, ACOs",
];
const projection = [
  ["2027 (Yr1)", "134,600", "$6.86M", "$1.55M", "$2.40M", "$11.17M"],
  ["2028", "150,000", "$6.75M", "$2.10M", "—", "$9.28M"],
  ["2029", "205,000", "$9.23M", "$2.85M", "—", "$12.60M"],
  ["2030", "310,000", "$11.53M", "$3.60M", "—", "$15.73M"],
  ["2031", "420,000", "$15.62M", "$4.40M", "—", "$20.74M"],
];

function SectionTable({ head, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">{head.map((h, i) => <th key={i} className={`px-4 py-3 font-semibold ${i > 0 && i === head.length - 1 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => <tr key={i} className="hover:bg-muted transition-colors">{r.map((c, j) => <td key={j} className={`px-4 py-3 ${j === 1 ? "font-mono font-semibold text-brand-700" : "text-muted-foreground"}`}>{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

export default function Monetization() {
  return (
    <div className="vc-fade-up max-w-6xl">
      <PageHeader
        title="Monetization & Pricing Model"
        subtitle="How Go MCO is sold: hybrid SaaS with implementation + PMPM, expanded through upsells, retainers, and secondary revenue angles."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadPdf("monetization", "GoMCO-Monetization-Model.pdf")} data-testid="download-monetization-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <a href="/docs/GoMCO-Monetization-Model.md" download><Button data-testid="download-monetization"><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Year-1 Contract Value</p><p className="text-2xl font-bold font-display mt-1 text-brand-700">$11.2M</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">5-Year TCV</p><p className="text-2xl font-bold font-display mt-1 text-brand-700">~$69.5M</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Base PMPM</p><p className="text-2xl font-bold font-display mt-1 text-brand-700">$4.25</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Sub. Gross Margin</p><p className="text-2xl font-bold font-display mt-1 text-brand-700">75–80%</p></Card>
      </div>

      <Card className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><DollarSign className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">RFP Pricing Grid — Answered</h2></div>
        <SectionTable head={["Cost Description", "Go MCO Offer", "Notes"]} rows={grid} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">PMPM Volume Tiers</h2></div>
          <SectionTable head={["Membership Band", "PMPM", "Annualized"]} rows={tiers} />
          <p className="text-xs text-muted-foreground mt-3">Billed on the greater of eligible members or a 100,000-member floor.</p>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Repeat className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Retainers & Managed Services</h2></div>
          <div className="space-y-2">
            {retainers.map(([n, p]) => (
              <div key={n} className="flex items-center justify-between rounded-md border border-border p-3">
                <span className="text-sm">{n}</span><span className="font-mono font-semibold text-brand-700 text-sm">{p}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><Layers className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Upsell Modules — "Buy Only What You Need"</h2></div>
        <SectionTable head={["Module", "Pricing", "Value"]} rows={upsells} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Additional Monetization Angles</h2></div>
          <ul className="space-y-2">{angles.map((a) => <li key={a} className="flex gap-2 text-sm text-muted-foreground"><span className="text-brand-600 font-bold">›</span>{a}</li>)}</ul>
        </Card>
        <Card className="p-6">
          <h2 className="font-display text-xl font-bold mb-4">Editions</h2>
          <div className="space-y-3">
            {[["Essentials", "Core CM + portals", "default"], ["Professional", "+ UM/QM + analytics (land here)", "Rising"], ["Enterprise", "+ AI, billing, interoperability, premium SLA", "Low"]].map(([n, d, tone]) => (
              <div key={n} className="rounded-md border border-border p-4">
                <div className="flex items-center justify-between"><span className="font-semibold">{n}</span><Badge tone={tone}>{n === "Professional" ? "Recommended landing" : n === "Enterprise" ? "Expansion target" : "Entry"}</Badge></div>
                <p className="text-sm text-muted-foreground mt-1">{d}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="font-display text-xl font-bold mb-4">Five-Year Financial Projection (Trillium)</h2>
        <SectionTable head={["Year", "Members", "Subscription", "Upsells", "Implementation", "Total"]} rows={projection} />
        <p className="text-sm text-muted-foreground mt-4">A single large MCO represents a <strong className="text-brand-700">$50–70M</strong> lifetime relationship. Base-only 5-year TCV is ~$52.4M; upsells + retainers add ~$17M.</p>
      </Card>
    </div>
  );
}
